import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumb from "../../components/Breadcrumb";
import ContentLoader from "../../utils/ContentLoader";
import { api } from "../../utils/apiClient";
import { API } from "../../config/constants";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setProfileInfo } from "../../redux/slice/getProfileInfo";


export default function ProfilePage() {
    // Get user info from Redux store instead of localStorage
    const profile = useSelector((state) => state.profile.profile);
    const userId = profile?.id;
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        gender: "",
        contact: "",
        icon: null,
        password: "",
        iconUrl: "",
    });
    const navigate = useNavigate();

    /* Fetch profile */
    useEffect(() => {
        if (!userId) {
            toast.error("User not found. Please login again.");
            return;
        }

        const fetchProfile = async () => {
            try {
                const res = await api.get(`/users/${userId}`);

                //console.log("user======>", res.data )
                const data = res.data;

                setFormData({
                    name: data.name || "",
                    email: data.email || "",
                    gender: data.gender || "",
                    contact: data.contact || "",
                    icon: null,
                    iconUrl: data.icon
                        ? `${API.IMAGE_BASE_URL}/${data.icon}`
                        : "/assets/images/logo-profile.png",
                    password: "",
                });
            } catch (err) {
                toast.error("Failed to load profile");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [userId]);

    /* Handle change */
    const handleChange = (e) => {
        const { name, value, files } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: files ? files[0] : value,
        }));

        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    /* Validation */
    const validate = () => {
        const err = {};
        if (!formData.name.trim()) err.name = "Name is required";
        if (!formData.email.trim()) err.email = "Email is required";
        if (!formData.contact.trim()) err.contact = "Contact is required";
        if (!formData.gender) err.gender = "Gender is required";

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    /* Submit */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        try {
            let payload;
            let headers;

            const cleanData = { ...formData };
            if (!cleanData.password) delete cleanData.password;

            if (cleanData.icon) {
                payload = new FormData();
                Object.keys(cleanData).forEach((key) => {
                    if (cleanData[key]) payload.append(key, cleanData[key]);
                });
                headers = { "Content-Type": "multipart/form-data" };
            } else {
                payload = cleanData;
                delete payload.icon;
                headers = { "Content-Type": "application/json" };
            }

            const res = await api.post(`/users/${userId}`, payload, { headers }); // ✅ store response
            const updatedUser = res.data.data;

            dispatch(setProfileInfo(updatedUser));
            toast.success("Profile updated successfully!");
            navigate("/");
        } catch (err) {
            toast.error(err.response?.data?.message || "Profile update failed!");
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <ContentLoader />;

    return (
        <>
            <Breadcrumb title="My Profile" />

            <div className="col-lg-12 mt-4">
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Profile Information</h5>
                    </div>

                    <div className="card-body">
                        <form className="row gy-4" onSubmit={handleSubmit}>

                            {/* PROFILE IMAGE CENTER */}
                            <div className="col-12 d-flex flex-column align-items-center">
                                <label className="form-label mb-2">Profile Picture</label>

                                {/* Clickable Avatar */}
                                <label
                                    style={{
                                        width: 110,
                                        height: 110,
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        border: "2px solid #e5e7eb",
                                        cursor: "pointer",
                                        display: "inline-block",
                                        marginBottom: 10,
                                    }}
                                >
                                    <img
                                        src={formData.previewImage || formData.iconUrl}
                                        alt="Profile"
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                    <input
                                        type="file"
                                        name="icon"
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => {
                                            handleChange(e);
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    previewImage: URL.createObjectURL(file),
                                                }));
                                            }
                                        }}
                                    />
                                </label>
                            </div>


                            {/* Name */}
                            <div className="col-md-6">
                                <label className="form-label">Full Name <span style={{ color: "red" }}>*</span></label>
                                <input
                                    type="text"
                                    name="name"
                                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>

                            {/* Email */}
                            <div className="col-md-6">
                                <label className="form-label">Email <span style={{ color: "red" }}>*</span></label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Gender */}
                            <div className="col-md-6">
                                <label className="form-label">Gender <span style={{ color: "red" }}>*</span></label>
                                <select
                                    name="gender"
                                    className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                                    value={formData.gender}
                                    onChange={handleChange}
                                >
                                    <option value="">Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                            </div>

                            {/* Contact */}
                            <div className="col-md-6">
                                <label className="form-label">Contact <span style={{ color: "red" }}>*</span></label>
                                <input
                                    type="text"
                                    name="contact"
                                    className={`form-control ${errors.contact ? "is-invalid" : ""}`}
                                    value={formData.contact}
                                    onChange={handleChange}
                                />
                                {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
                            </div>

                            {/* Submit */}
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <button className="btn btn-primary-600">
                                    Update Profile
                                </button>
                            </div>

                        </form>
                    </div>

                </div>
            </div>
        </>
    );
}
